import { Avatar, Box, ButtonBase, Divider, Hidden, Tooltip, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import DarkModeIcon from '@material-ui/icons/Brightness3';
import DashboardIcon from '@material-ui/icons/Dashboard';
import PowerSettingsNewOutlinedIcon from '@material-ui/icons/PowerSettingsNewOutlined';
import LightModeIcon from '@material-ui/icons/WbSunny';
import { ReactComponent as StandartBadge } from 'assets/bagdes/medal-2.svg';
import { ReactComponent as ProfessionalBadge } from 'assets/bagdes/medal-3.svg';
import { ReactComponent as PremiumBadge } from 'assets/bagdes/medal-4.svg';
import { ReactComponent as PrepaidBadge } from 'assets/bagdes/medal.svg';
import { Link } from 'components';
import { defaultAvatar } from 'config';
import { AuthUserProps, useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { useCurrentPlanQuery } from 'graphql/generated';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminMenuItems, EmployerMenuItems, JobseekerMenuItems } from './menu-items';
import { useStyle } from './profile-menu.style';

type ProfileMenuProps = {
    user: AuthUserProps;
    logOut: () => void;
};

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ user, logOut }) => {
    const classes = useStyle();
    const { t } = useTranslation();
    const { isEmployer, isAdmin } = useAuth();
    const { isDarkMode, toggleTheme } = useSettings();
    const { data: pricingData } = useCurrentPlanQuery({ skip: !isEmployer, fetchPolicy: 'cache-and-network' });
    const currentPlan = pricingData?.currentPlan?.plan;

    const badgeMap = {
        [1]: <PrepaidBadge className={classes.prepaid} />,
        [2]: <StandartBadge className={classes.standart} />,
        [3]: <PremiumBadge className={classes.premium} />,
        [4]: <ProfessionalBadge className={classes.professional} />,
    };

    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const renderMenuItems = (): React.ReactElement => {
        if (isEmployer) {
            return <EmployerMenuItems {...{ handleClose }} />;
        }
        if (isAdmin) {
            return <AdminMenuItems {...{ handleClose }} />;
        }
        return <JobseekerMenuItems {...{ handleClose }} />;
    };

    return (
        <div style={{ position: 'relative' }}>
            <Hidden smDown>
                <Button onClick={(event): void => handleClick(event)} className={classes.toggleButton}>
                    <Typography style={{ margin: '0 8px', fontWeight: 600 }}>{user?.firstName}</Typography>
                    <Avatar style={{ width: 35, height: 35 }} src={user?.avatar?.url || defaultAvatar} />
                </Button>
            </Hidden>
            <Hidden mdUp>
                <ButtonBase onClick={(event): void => handleClick(event)} className={classes.toggleButton}>
                    <Avatar style={{ width: 35, height: 35 }} src={user?.avatar?.url || defaultAvatar} />
                </ButtonBase>
            </Hidden>

            <Popover
                id={id}
                open={open}
                classes={{
                    paper: classes.root,
                }}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <div className={classes.userDetails}>
                    <Link to={`/d/profile`}>
                        <Button onClick={handleClose} fullWidth className={classes.profileButton}>
                            <div style={{ display: 'flex', flexDirection: 'row' }}>
                                <Avatar className={classes.avatar} src={user?.avatar?.url || defaultAvatar} />
                                <div className={classes.userInfo}>
                                    <Typography
                                        className={classes.userName}
                                    >{`${user?.firstName} ${user?.lastName}`}</Typography>
                                    <Typography variant="h6" color="textSecondary" className={classes.userRole}>
                                        {t(`roles:${user?.role}`)}{' '}
                                        {isEmployer && currentPlan && (
                                            <Tooltip title={currentPlan.name}>{badgeMap[currentPlan.order]}</Tooltip>
                                        )}
                                    </Typography>
                                </div>
                            </div>
                        </Button>
                    </Link>
                </div>
                <Divider />
                <div>
                    <Box className={classes.userMenu}>
                        <Link to="/d/home">
                            <Button
                                onClick={handleClose}
                                startIcon={
                                    <Box className={classes.iconContainer}>
                                        <DashboardIcon />
                                    </Box>
                                }
                                className={classes.userMenuItem}
                            >
                                <Typography className={classes.userMenuItemText}>
                                    {t('mainProfileMenu:dashboard')}
                                </Typography>
                            </Button>
                        </Link>
                        {renderMenuItems()}
                    </Box>
                    <Divider />
                    <Box className={classes.userMenu}>
                        <Button
                            onClick={toggleTheme}
                            startIcon={
                                <Box className={classes.iconContainer}>
                                    {isDarkMode ? (
                                        <LightModeIcon />
                                    ) : (
                                        <DarkModeIcon style={{ transform: 'rotate(45deg)' }} />
                                    )}
                                </Box>
                            }
                            className={classes.userMenuItem}
                        >
                            <Typography className={classes.userMenuItemText}>
                                {isDarkMode ? t('mainProfileMenu:lightTheme') : t('mainProfileMenu:darkTheme')}
                            </Typography>
                        </Button>
                        <Button
                            onClick={(): void => logOut()}
                            startIcon={
                                <Box className={classes.iconContainer}>
                                    <PowerSettingsNewOutlinedIcon />
                                </Box>
                            }
                            className={classes.userMenuItem}
                        >
                            <Typography className={classes.userMenuItemText}>{t('mainProfileMenu:logout')}</Typography>
                        </Button>
                    </Box>
                </div>
            </Popover>
        </div>
    );
};
